import React from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { formatRelativeDate } from "../utils/dateUtils";
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import RestoreIcon from '@mui/icons-material/Restore';
import RateReviewIcon from '@mui/icons-material/RateReview';

/**
 * SectionSourceCard
 * Props:
 * - fileName: string
 * - page: number|string
 * - text: string
 * - onDelete: function
 */
const SectionSourceCard = ({
  fileName = "{sourceFileName}",
  page,
  text = "",
  sectionName,
  date_uploaded,
  onDelete = null,
  onRefresh = null,
  onClick, // NEW: optional onClick for the card
  showRetrievedInfo = false,
  needReview = false,
  uploading = false,
  disableActions = false, // NEW: disable delete and refresh buttons
  onResetToWaiting = null, // DEV: callback to reset document to waiting state
  documentId = null, // DEV: document ID for dev actions
  onForceReview = null, // Callback to force document into review state
}) => {
  const retrievedInfo = (needReview, uploading, date_uploaded) => {
      if(needReview) {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 0.5,
            }}
          > 
            <WarningIcon
              sx={{
                width: 16,
                height: 16,
                my: 0.5,
                mx: 0.25,
                color: "#F97316",
              }}
            />
            <Typography sx={{ color: "#F97316", fontSize: 13 }}>
              Review sections to regenerate
            </Typography>
          </Box>
        )
      } else if (uploading) {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 0.5,
            }}
          > 
            <Tooltip
              title="Uploading a new file can take up to 1 hour. We’ll email you when it’s ready"
              placement="bottom-start"
            >
              <InfoIcon
                sx={{
                  width: 16,
                  height: 16,
                  my: 0.5,
                  mx: 0.25,
                  color: "#6B7280",
                }}
              />
            </Tooltip>
            <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
              Uploading replacement file
            </Typography>
          </Box>
        )
      } else {
        return (
          <Typography sx={{ color: "#64748B", fontSize: 13 }}>
            Retrieved from {formatRelativeDate(date_uploaded)}
          </Typography>
        )
      }
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        cursor: onClick ? "pointer" : "default",
        mb: text ? 3 : 1.5,
        px: 0,
        mt: 1.5,
        position: "relative",
        height: '100%',
        minHeight: 25,
        transition: "all 0.15s ease",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(16,30,54,0.12)",
          borderColor: "#CBD5E1",
          background: onClick ? "#F1F5F9" : undefined,
        },
      }}
      onClick={e => {
        // Prevent click from firing if delete button is clicked
        if (e.target.closest(".section-source-delete-btn")) return;
        if (onClick) onClick(e);
      }}
    >
      {/* Blue vertical bar */}
      <Box
        sx={{
          width: 4,
          borderRadius: "2px 0 0 2px",
          background: "#1F477D",
          height: "100%",
          minHeight: text ? sectionName ? showRetrievedInfo ? "120px" : "90px" : showRetrievedInfo ? "100px" : "70px" : showRetrievedInfo ? "48px" : "32px",
          mr: 1.5,
          flexShrink: 0,
        }}
      />
      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, pr: 1, height: "100%", width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.25, height: "100%", mb: 0.5, width: "100%"   }}>
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", height: "100%", width: "100%" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: "#1E293B",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                letterSpacing: 0.01,
              }}
              title={`${fileName}`}
            >
              {fileName}
            </Typography>
            {page && (              
              <Typography sx={{ fontSize: 14, color: "#64748B" }} title={`Page ${page}`}>
                Page {page}
              </Typography>
            )}
            {onRefresh && (
              <Tooltip title={disableActions ? "Document is being processed" : "Refresh source"}>
                <span>
                  <IconButton 
                    onClick={(e) => {
                      if (!disableActions) {
                        onRefresh()
                        e.stopPropagation()
                      }
                    }}
                    disabled={disableActions}
                    sx={{
                      color: disableActions ? "#D1D5DB" : "#94A3B8",
                      ml: 0.5,
                      width: 20,
                      height: 20,
                      "&:hover": {
                        color: disableActions ? "#D1D5DB" : "#1F477D",
                      },
                      transition: "all 0.15s ease",
                      cursor: disableActions ? "not-allowed" : "pointer",
                    }}>
                    <SyncAltIcon 
                      sx={{
                        width: 20,
                        height: 20,
                        margin: 0.5,
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {process.env.NODE_ENV === 'development' && onForceReview && false && (
              <Tooltip title="Mark for review">
                <span>
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation()
                      onForceReview(documentId)
                    }}
                    sx={{
                      color: "#10B981",
                      ml: 0.5,
                      width: 20,
                      height: 20,
                      "&:hover": {
                        color: "#059669",
                      },
                      transition: "all 0.15s ease",
                    }}>
                    <RateReviewIcon 
                      sx={{
                        width: 20,
                        height: 20,
                        margin: 0.5,
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {process.env.NODE_ENV === 'development' && onResetToWaiting && false && (
              <Tooltip title="[DEV] Reset to waiting state">
                <span>
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation()
                      onResetToWaiting(documentId)
                    }}
                    sx={{
                      color: "#F59E0B",
                      ml: 0.5,
                      width: 20,
                      height: 20,
                      "&:hover": {
                        color: "#D97706",
                      },
                      transition: "all 0.15s ease",
                    }}>
                    <RestoreIcon 
                      sx={{
                        width: 20,
                        height: 20,
                        margin: 0.5,
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title={disableActions ? "Document is being processed" : "Delete source"}>
                <span>
                  <IconButton 
                    onClick={(e) => {
                      if (!disableActions) {
                        onDelete(e)
                      }
                    }}
                    disabled={disableActions}
                    sx={{
                      color: disableActions ? "#D1D5DB" : "#94A3B8",
                      ml: 0.5,
                      width: 20,
                      height: 20,
                      "&:hover": {
                        color: disableActions ? "#D1D5DB" : "#E11D48",
                      },
                      transition: "all 0.15s ease",
                      cursor: disableActions ? "not-allowed" : "pointer",
                    }}
                  >
                  <DeleteOutlineIcon 
                    sx={{
                      width: 20,
                      height: 20,
                      margin: 0.5,
                    }}
                  />
                </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          {sectionName && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: "#1E293B",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                letterSpacing: 0.01,
              }}
              title={`${fileName}`}
            >
              {sectionName}
            </Typography>
          )}
        </Box>
        {text && (
          <Typography
            sx={{
              color: "#64748B",
              fontSize: 13,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.5,
              pr: 1,
              fontWeight: 400,
            }}
            title={text}
          >
            {text}
          </Typography>
        )}
        {showRetrievedInfo && (
          retrievedInfo(needReview, uploading, date_uploaded)
        )}
      </Box>
    </Box>
  );
};

export default SectionSourceCard;